import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import FicheSurveillanceSection, { SurveillanceFiche, emptySurveillanceFiche } from './FicheSurveillanceSection';

interface DossierRoseData {
  id?: string;
  patient_id: string;
  // Structure sanitaire
  departement: string;
  zone_sanitaire: string;
  commune: string;
  arrondissement: string;
  structure_sanitaire: string;
  // Dossier obstétrical
  no_dossier: string;
  no_accouchement: string;
  annee: number | null;
  date_accouchement: string;
  date_entree: string;
  date_sortie: string;
  nom_jeune_fille: string;
  prenoms: string;
  date_naissance: string;
  nationalite: string;
  profession: string;
  gs: string;
  rh: string;
  taille: string;
  village_quartier: string;
  carre_no: string;
  rue_no: string;
  maison: string;
  telephone: string;
  // Procréateur
  procreateur_nom: string;
  procreateur_prenoms: string;
  procreateur_profession: string;
  procreateur_gs: string;
  procreateur_rh: string;
  procreateur_electrophorese: string;
  procreateur_serologie: string;
  procreateur_contact: string;
  procreateur_personne_contacter: string;
  procreateur_adresse: string;
  procreateur_tel: string;
  procreateur_venue_elle_meme: boolean;
  procreateur_referee: boolean;
  procreateur_referee_par: string;
  // Antécédents
  maladies_operations: string;
  traitements_en_cours: string;
  allergies: string;
  transfusions_anterieures: boolean;
  transfusions_nombre: number | null;
  gestite: number | null;
  parite: number | null;
  nombre_avortements: number | null;
  nombre_enfants_vivants: number | null;
  nombre_enfants_decede: number | null;
  ddr: string;
  dpa: string;
  // Facteurs de risque
  facteurs_risque: string[];
  autres_facteurs: string;
  // Examens complémentaires
  examens_gs: string;
  examens_rh: string;
  coobs_direct: string;
  tpha: string;
  vdrl: string;
  autres_examens: string;
  electrophorese: string;
  toxo_igg: string;
  toxo_igm: string;
  rubeole_igg: string;
  rubeole_igm: string;
  glycemie_jeun: string;
  hiv1: string;
  hiv2: string;
  ecbu: string;
  taux_hemoglobine: string;
  hematocrite: string;
  plaquettes: string;
  hepatite_b: string;
  // HIV Positif
  hiv_positif_arv: boolean;
  syphilis_positif_traitement: boolean;
  mise_sous_ctm: boolean;
  // VAT
  vat1: boolean;
  vat2: boolean;
  vat3: boolean;
  vat4: boolean;
  vat5: boolean;
  // Droits fondamentaux
  droits_fondamentaux: string[];
  // Plan accouchement
  plan_lieu_accouchement: string;
  plan_accoucheur_qualifie: string;
  plan_contact_accoucheur: string;
  plan_mode_transport: string;
  plan_accompagnement: string;
  plan_garde_enfants: string;
  plan_decideur_absence_mari: string;
  plan_epargne_accouchement: string;
  plan_donneur_sang: string;
  // Autres soins
  info_pf: boolean;
  reference_cpn: boolean;
  reference_accouchement: boolean;
  // Classe des mères
  classe_1er_trimestre: boolean;
  classe_2e_trimestre: boolean;
  classe_3e_trimestre: boolean;
  classe_accouchement_recent: boolean;
  classe_accouchement_tardif: boolean;
  // Consultation prénatale
  cpn_date_premiere_visite: string;
  cpn_trimestre: string;
  cpn_date_heure_arrivee: string;
  cpn_heure_cpnr: string;
  cpn_prestataire_identite: string;
  cpn_prestataire_qualification: string;
  cpn_terme_grossesse: string;
  cpn_resultat_echographie: string;
  cpn_plainte: string;
  // Etat général
  cpn_ta: string;
  cpn_pouls: string;
  cpn_temperature: string;
  cpn_muqueuse: string;
  cpn_omi: string;
  cpn_conscience: string;
  cpn_poids: string;
  cpn_imc: string;
  // Test urine
  cpn_urine_albumine: string;
  cpn_urine_sucre: string;
  cpn_urine_nitrite: string;
  cpn_urine_leucocyte: string;
  // Examens physiques
  cpn_examen_seins: string;
  cpn_inspection: string;
  cpn_mensuration_hu: string;
  cpn_palpation_presentation: string;
  cpn_palpation_points_ureteraux: string;
  cpn_auscultation_bdcf: string;
  cpn_etat_vulve: string;
  cpn_pose_speculum: string;
  cpn_toucher_vaginal: string;
  cpn_examen_bassin: string;
  // Diagnostic
  cpn_diagnostic: string;
  // Prévention
  cpn_tpi_sp_dose1: string;
  cpn_tpi_sp_dose2: string;
  cpn_tpi_sp_dose3: string;
  cpn_tpi_sp_dose4: string;
  cpn_tpi_sp_dose5: string;
  cpn_deparasitage: string;
  cpn_fer_200mg: string;
  cpn_foldine_5mg: string;
  cpn_dormi_sous_mild: boolean;
  // Conseils et planification
  cpn_conseils_nutritionnels: string;
  cpn_signes_danger: string;
  cpn_counseling_pf: string;
  cpn_methode_choisie: string;
  cpn_conduite_a_tenir: string;
  cpn_rdv_cpn: string;
  cpn_rdv_classe_meres: string;
  cpn_recherche_active: boolean;
  // === SECTION 14: Suivi de l'Accouchement ===
  // État du nouveau-né
  etat_nouveau_ne_vivant: boolean;
  etat_nouveau_ne_respiration_spontanee: boolean;
  etat_nouveau_ne_reanimation: boolean;
  etat_nouveau_ne_duree_reanimation: string;
  etat_nouveau_ne_ictere: boolean;
  apgar_coeur_1min: string;
  apgar_coeur_5min: string;
  apgar_coeur_10min: string;
  apgar_respiration_1min: string;
  apgar_respiration_5min: string;
  apgar_respiration_10min: string;
  apgar_tonus_1min: string;
  apgar_tonus_5min: string;
  apgar_tonus_10min: string;
  apgar_couleur_1min: string;
  apgar_couleur_5min: string;
  apgar_couleur_10min: string;
  apgar_reflexes_1min: string;
  apgar_reflexes_5min: string;
  apgar_reflexes_10min: string;
  apgar_total_1min: string;
  apgar_total_5min: string;
  apgar_total_10min: string;
  poids_nouveau_ne: string;
  taille_nouveau_ne: string;
  pt_nouveau_ne: string;
  pc_nouveau_ne: string;
  sexe_nouveau_ne: string;
  mort_ne_macre: boolean;
  mort_ne_macre_1erdegre: string;
  mort_ne_macre_2emedegre: string;
  mort_ne_macre_3emedegre: string;
  mort_ne_frais: boolean;
  mort_ne_frais_1erdegre: string;
  mort_ne_frais_2emedegre: string;
  mort_ne_frais_3emedegre: string;
  semaine_grossesse: string;
  // Soins au nouveau-né
  soins_mise_sein_precoce: boolean;
  soins_collyre: boolean;
  soins_arv_mere_seropositive: boolean;
  soins_arv_vitamine: string;
  soins_arv_k1: string;
  soins_anomalies: boolean;
  soins_anomalies_details: string;
  soins_examen_systematique: boolean;
  // Délivrance
  delivrance_heure: string;
  delivrance_naturelle: boolean;
  delivrance_artificielle: boolean;
  delivrance_assistee_gatpa: boolean;
  delivrance_oxytocine_10ui: boolean;
  delivrance_traction_controlee: boolean;
  delivrance_massage_uterin: boolean;
  delivrance_hemorragie: boolean;
  delivrance_quantite_sang: string;
  // Placenta
  placenta_cotyledons_integres: boolean;
  placenta_presence_cupule: boolean;
  placenta_examen_face_maternelle: boolean;
  placenta_examen_face_foetale: boolean;
  placenta_grand_cote: string;
  placenta_insertion_cordon: string;
  placenta_petit_cote: string;
  placenta_longueur_cordon: string;
  placenta_poids: string;
  // Issue
  issue_femme_referee: boolean;
  issue_femme_decedee: boolean;
  issue_nouveau_ne_referee: boolean;
  issue_nouveau_ne_decede: boolean;
  // Sensibilisation
  sensib_pf: boolean;
  sensib_methode_choisie: string;
  sensib_allaitement: boolean;
  sensib_acceptation: boolean;
  sensib_si_non_motif: string;
  sensib_carte_infantile_remplie: boolean;
  sensib_vitamine_a_accouchee: boolean;
  // Section 15: Fiche de surveillance
  surveillance_constantes: SurveillanceFiche | null;
}

interface GrossesseAnterieure {
  id?: string;
  dossier_rose_id?: string;
  annee: string;
  grossesse: string;
  evolution: string;
  poids: string;
  sexe: string;
  etat_enfants: string;
}

const emptyDossierRose: DossierRoseData = {
  patient_id: '',
  departement: '', zone_sanitaire: '', commune: '', arrondissement: '', structure_sanitaire: '',
  no_dossier: '', no_accouchement: '', annee: null, date_accouchement: '', date_entree: '', date_sortie: '',
  nom_jeune_fille: '', prenoms: '', date_naissance: '', nationalite: '', profession: '',
  gs: '', rh: '', taille: '', village_quartier: '', carre_no: '', rue_no: '', maison: '', telephone: '',
  procreateur_nom: '', procreateur_prenoms: '', procreateur_profession: '', procreateur_gs: '', procreateur_rh: '',
  procreateur_electrophorese: '', procreateur_serologie: '', procreateur_contact: '',
  procreateur_personne_contacter: '', procreateur_adresse: '', procreateur_tel: '',
  procreateur_venue_elle_meme: false, procreateur_referee: false, procreateur_referee_par: '',
  maladies_operations: '', traitements_en_cours: '', allergies: '',
  transfusions_anterieures: false, transfusions_nombre: null,
  gestite: null, parite: null, nombre_avortements: null, nombre_enfants_vivants: null, nombre_enfants_decede: null,
  ddr: '', dpa: '',
  facteurs_risque: [], autres_facteurs: '',
  examens_gs: '', examens_rh: '', coobs_direct: '', tpha: '', vdrl: '', autres_examens: '',
  electrophorese: '', toxo_igg: '', toxo_igm: '', rubeole_igg: '', rubeole_igm: '',
  glycemie_jeun: '', hiv1: '', hiv2: '', ecbu: '', taux_hemoglobine: '', hematocrite: '', plaquettes: '', hepatite_b: '',
  hiv_positif_arv: false, syphilis_positif_traitement: false, mise_sous_ctm: false,
  vat1: false, vat2: false, vat3: false, vat4: false, vat5: false,
  droits_fondamentaux: [],
  plan_lieu_accouchement: '', plan_accoucheur_qualifie: '', plan_contact_accoucheur: '',
  plan_mode_transport: '', plan_accompagnement: '', plan_garde_enfants: '',
  plan_decideur_absence_mari: '', plan_epargne_accouchement: '', plan_donneur_sang: '',
  info_pf: false, reference_cpn: false, reference_accouchement: false,
  classe_1er_trimestre: false, classe_2e_trimestre: false, classe_3e_trimestre: false,
  classe_accouchement_recent: false, classe_accouchement_tardif: false,
  // Consultation prénatale
  cpn_date_premiere_visite: '',
  cpn_trimestre: '',
  cpn_date_heure_arrivee: '',
  cpn_heure_cpnr: '',
  cpn_prestataire_identite: '',
  cpn_prestataire_qualification: '',
  cpn_terme_grossesse: '',
  cpn_resultat_echographie: '',
  cpn_plainte: '',
  cpn_ta: '',
  cpn_pouls: '',
  cpn_temperature: '',
  cpn_muqueuse: '',
  cpn_omi: '',
  cpn_conscience: '',
  cpn_poids: '',
  cpn_imc: '',
  cpn_urine_albumine: '',
  cpn_urine_sucre: '',
  cpn_urine_nitrite: '',
  cpn_urine_leucocyte: '',
  cpn_examen_seins: '',
  cpn_inspection: '',
  cpn_mensuration_hu: '',
  cpn_palpation_presentation: '',
  cpn_palpation_points_ureteraux: '',
  cpn_auscultation_bdcf: '',
  cpn_etat_vulve: '',
  cpn_pose_speculum: '',
  cpn_toucher_vaginal: '',
  cpn_examen_bassin: '',
  cpn_diagnostic: '',
  cpn_tpi_sp_dose1: '',
  cpn_tpi_sp_dose2: '',
  cpn_tpi_sp_dose3: '',
  cpn_tpi_sp_dose4: '',
  cpn_tpi_sp_dose5: '',
  cpn_deparasitage: '',
  cpn_fer_200mg: '',
  cpn_foldine_5mg: '',
  cpn_dormi_sous_mild: false,
  cpn_conseils_nutritionnels: '',
  cpn_signes_danger: '',
  cpn_counseling_pf: '',
  cpn_methode_choisie: '',
  cpn_conduite_a_tenir: '',
  cpn_rdv_cpn: '',
  cpn_rdv_classe_meres: '',
  cpn_recherche_active: false,
  // === SECTION 14: Suivi de l'Accouchement ===
  etat_nouveau_ne_vivant: false,
  etat_nouveau_ne_respiration_spontanee: false,
  etat_nouveau_ne_reanimation: false,
  etat_nouveau_ne_duree_reanimation: '',
  etat_nouveau_ne_ictere: false,
  apgar_coeur_1min: '', apgar_coeur_5min: '', apgar_coeur_10min: '',
  apgar_respiration_1min: '', apgar_respiration_5min: '', apgar_respiration_10min: '',
  apgar_tonus_1min: '', apgar_tonus_5min: '', apgar_tonus_10min: '',
  apgar_couleur_1min: '', apgar_couleur_5min: '', apgar_couleur_10min: '',
  apgar_reflexes_1min: '', apgar_reflexes_5min: '', apgar_reflexes_10min: '',
  apgar_total_1min: '', apgar_total_5min: '', apgar_total_10min: '',
  poids_nouveau_ne: '',
  taille_nouveau_ne: '',
  pt_nouveau_ne: '',
  pc_nouveau_ne: '',
  sexe_nouveau_ne: '',
  mort_ne_macre: false,
  mort_ne_macre_1erdegre: '',
  mort_ne_macre_2emedegre: '',
  mort_ne_macre_3emedegre: '',
  mort_ne_frais: false,
  mort_ne_frais_1erdegre: '',
  mort_ne_frais_2emedegre: '',
  mort_ne_frais_3emedegre: '',
  semaine_grossesse: '',
  soins_mise_sein_precoce: false,
  soins_collyre: false,
  soins_arv_mere_seropositive: false,
  soins_arv_vitamine: '',
  soins_arv_k1: '',
  soins_anomalies: false,
  soins_anomalies_details: '',
  soins_examen_systematique: false,
  delivrance_heure: '',
  delivrance_naturelle: false,
  delivrance_artificielle: false,
  delivrance_assistee_gatpa: false,
  delivrance_oxytocine_10ui: false,
  delivrance_traction_controlee: false,
  delivrance_massage_uterin: false,
  delivrance_hemorragie: false,
  delivrance_quantite_sang: '',
  placenta_cotyledons_integres: false,
  placenta_presence_cupule: false,
  placenta_examen_face_maternelle: false,
  placenta_examen_face_foetale: false,
  placenta_grand_cote: '',
  placenta_insertion_cordon: '',
  placenta_petit_cote: '',
  placenta_longueur_cordon: '',
  placenta_poids: '',
  issue_femme_referee: false,
  issue_femme_decedee: false,
  issue_nouveau_ne_referee: false,
  issue_nouveau_ne_decede: false,
  sensib_pf: false,
  sensib_methode_choisie: '',
  sensib_allaitement: false,
  sensib_acceptation: false,
  sensib_si_non_motif: '',
  sensib_carte_infantile_remplie: false,
  sensib_vitamine_a_accouchee: false,
  surveillance_constantes: null,
};

const FACTEURS_RISQUE_OPTIONS = [
  'Age < 16 ans',
  'Age > 35 ans',
  'Taille < 1m50',
  'Boiterie',
  'Parité > 6',
  'Césarienne au dernier accouchement',
  'Mort-né au dernier accouchement',
  'Drépanocytose SS ou SC',
  'HTA connue',
  'Fausses couches répétées',
  'Diabète',
];

const DROITS_FONDAMENTAUX_OPTIONS = [
  "Droit à l'information",
  "Droit d'accès",
  'Droit au choix',
  'Droit à la sécurité',
  'Droit à la confidentialité et au secret professionnel',
  'Droit au confort',
  'Droit à la dignité',
  "Droit à l'assurance et aux avantages sociaux",
  'Droit à la continuité des soins',
  "Droit d'exprimer son opinion",
];

interface Props {
  patientId: string;
  userId: string;
  onSaved: () => void;
}

export default function DossierRoseForm({ patientId, userId, onSaved }: Props) {
  const [dossier, setDossier] = useState<DossierRoseData>({ ...emptyDossierRose, patient_id: patientId });
  const [grossesses, setGrossesses] = useState<GrossesseAnterieure[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [surveillance, setSurveillance] = useState<SurveillanceFiche>({ ...emptySurveillanceFiche });
  const [accouchementEffectue, setAccouchementEffectue] = useState(false);
  const [marquageAccouchementLoading, setMarquageAccouchementLoading] = useState(false);
  const offlineMutation = useOfflineMutation<any>();

  useEffect(() => {
    loadDossierRose();
    checkAccouchementFait();
  }, [patientId]);

  const checkAccouchementFait = async () => {
    try {
      const { data } = await supabase
        .from('consultations')
        .select('id')
        .eq('patient_id', patientId)
        .eq('accouchement_fait', true)
        .limit(1);
      if (data && data.length > 0) {
        setAccouchementEffectue(true);
      }
    } catch (err) {
      console.error('Erreur vérification accouchement:', err);
    }
  };

  const loadDossierRose = async () => {
    setLoading(true);
    try {
      const { data: dossierData } = await supabase
        .from('dossiers_roses')
        .select('*')
        .eq('patient_id', patientId)
        .maybeSingle();

      if (dossierData) {
        setDossier({
          ...emptyDossierRose,
          ...dossierData,
          patient_id: patientId,
          facteurs_risque: dossierData.facteurs_risque || [],
          droits_fondamentaux: dossierData.droits_fondamentaux || [],
        });

        const { data: grossessesData } = await supabase
          .from('grossesses_anterieures')
          .select('*')
          .eq('dossier_rose_id', dossierData.id)
          .order('annee', { ascending: false });

        if (grossessesData) setGrossesses(grossessesData);

        if (dossierData.surveillance_constantes) {
          const parsed = typeof dossierData.surveillance_constantes === 'string'
            ? JSON.parse(dossierData.surveillance_constantes)
            : dossierData.surveillance_constantes;
          setSurveillance({ ...emptySurveillanceFiche, ...parsed });
        } else {
          setSurveillance({ ...emptySurveillanceFiche });
        }
      }
    } catch (err) {
      console.error('Erreur chargement dossier rose:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string | boolean | number | null) => {
    setDossier(prev => ({ ...prev, [field]: value }));
  };

  const toggleFacteurRisque = (facteur: string) => {
    setDossier(prev => {
      const current = prev.facteurs_risque || [];
      if (current.includes(facteur)) {
        return { ...prev, facteurs_risque: current.filter(f => f !== facteur) };
      }
      return { ...prev, facteurs_risque: [...current, facteur] };
    });
  };

  const toggleDroitFondamental = (droit: string) => {
    setDossier(prev => {
      const current = prev.droits_fondamentaux || [];
      if (current.includes(droit)) {
        return { ...prev, droits_fondamentaux: current.filter(d => d !== droit) };
      }
      return { ...prev, droits_fondamentaux: [...current, droit] };
    });
  };

  const addGrossesse = () => {
    setGrossesses(prev => [...prev, {
      annee: '',
      grossesse: '',
      evolution: '',
      poids: '',
      sexe: '',
      etat_enfants: '',
    }]);
  };

  const removeGrossesse = (index: number) => {
    setGrossesses(prev => prev.filter((_, i) => i !== index));
  };

  const updateGrossesse = (index: number, field: string, value: string) => {
    setGrossesses(prev => prev.map((g, i) => i === index ? { ...g, [field]: value } : g));
  };

  const handleMarquerAccouchement = async () => {
    setMarquageAccouchementLoading(true);
    try {
      await offlineMutation.mutate({
        table: 'consultations',
        operation: 'UPDATE',
        data: { accouchement_fait: true },
        metadata: { summary: `Marquage accouchement — Patient ${patientId}` },
        onlineExecutor: async () => {
          const { data: consultationsExistantes } = await supabase
            .from('consultations')
            .select('id')
            .eq('patient_id', patientId)
            .limit(1);

          if (consultationsExistantes && consultationsExistantes.length > 0) {
            await supabase
              .from('consultations')
              .update({ accouchement_fait: true })
              .eq('id', consultationsExistantes[0].id);
            return consultationsExistantes[0];
          } else {
            const { data: newCons } = await supabase
              .from('consultations')
              .insert({
                patient_id: patientId,
                medecin_id: userId,
                date_consultation: new Date().toISOString().split('T')[0],
                statut: 'termine',
                accouchement_fait: true,
              })
              .select()
              .single();
            return newCons;
          }
        },
      });

      setAccouchementEffectue(true);
      setSaveMessage(offlineMutation.state.isOffline
        ? 'Accouchement marqué localement. Synchronisation au retour réseau.'
        : 'Accouchement marqué comme fait. Les sections post-accouchement sont maintenant accessibles.');
      setTimeout(() => setSaveMessage(''), 4000);
    } catch (err) {
      console.error('Erreur marquage accouchement:', err);
      setSaveMessage('Erreur lors du marquage de l\'accouchement.');
    } finally {
      setMarquageAccouchementLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      const payload: Record<string, unknown> = { ...dossier };
      delete payload.id;
      payload.surveillance_constantes = surveillance;

      await offlineMutation.mutate({
        table: 'dossiers_roses',
        operation: dossier.id ? 'UPDATE' : 'INSERT',
        recordId: dossier.id || undefined,
        data: payload,
        expectedUpdatedAt: dossier.id ? (dossier as any).updated_at || null : null,
        metadata: { summary: `Dossier Rose — Patient ${patientId}` },
        onlineExecutor: async () => {
          let dossierId = dossier.id;
          if (dossierId) {
            await supabase.from('dossiers_roses').update({ ...payload, updated_by: userId }).eq('id', dossierId);
          } else {
            const { data: newDossier } = await supabase
              .from('dossiers_roses')
              .insert({ ...payload, created_by: userId })
              .select('id')
              .single();
            if (newDossier) {
              dossierId = newDossier.id;
              setDossier(prev => ({ ...prev, id: dossierId }));
            }
          }

          if (dossierId) {
            await supabase.from('grossesses_anterieures').delete().eq('dossier_rose_id', dossierId);
            if (grossesses.length > 0) {
              const toInsert = grossesses.map(g => ({ ...g, dossier_rose_id: dossierId }));
              await supabase.from('grossesses_anterieures').insert(toInsert);
            }
          }
          return { id: dossierId, ...payload };
        },
      });

      setSaveMessage(offlineMutation.state.isOffline
        ? 'Dossier rose sauvegardé localement. Synchronisation au retour réseau.'
        : 'Dossier rose enregistré avec succès !');
      setTimeout(() => setSaveMessage(''), 3000);
      onSaved();
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setSaveMessage('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-3xl text-rose-500 animate-spin"></i>
        <p className="mt-3 text-gray-500">Chargement du dossier rose...</p>
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 bg-white text-gray-800 placeholder-gray-400";
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-8">
      {saveMessage && (
        <div className={`px-4 py-3 rounded-lg text-sm font-medium ${saveMessage.includes('Erreur') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
          <i className={`${saveMessage.includes('Erreur') ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} mr-2`}></i>
          {saveMessage}
        </div>
      )}

      {/* SECTION 1: Structure sanitaire */}
      <section>
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-hospital-line text-rose-500"></i>
          Structure Sanitaire
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Département</label>
            <input type="text" className={inputClass} value={dossier.departement} onChange={e => handleChange('departement', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Zone sanitaire</label>
            <input type="text" className={inputClass} value={dossier.zone_sanitaire} onChange={e => handleChange('zone_sanitaire', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Commune</label>
            <input type="text" className={inputClass} value={dossier.commune} onChange={e => handleChange('commune', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Arrondissement</label>
            <input type="text" className={inputClass} value={dossier.arrondissement} onChange={e => handleChange('arrondissement', e.target.value)} />
          </div>
          <div className="sm:col-span-2 lg:col-span-2">
            <label className={labelClass}>Structure sanitaire</label>
            <input type="text" className={inputClass} value={dossier.structure_sanitaire} onChange={e => handleChange('structure_sanitaire', e.target.value)} />
          </div>
        </div>
      </section>

      {/* SECTION 2: Dossier Obstétrical */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-folder-user-line text-rose-500"></i>
          Dossier Obstétrical
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div><label className={labelClass}>N° Dossier</label><input type="text" className={inputClass} value={dossier.no_dossier} onChange={e => handleChange('no_dossier', e.target.value)} /></div>
          <div><label className={labelClass}>N° Accouchement</label><input type="text" className={inputClass} value={dossier.no_accouchement} onChange={e => handleChange('no_accouchement', e.target.value)} /></div>
          <div><label className={labelClass}>Année</label><input type="number" className={inputClass} value={dossier.annee || ''} onChange={e => handleChange('annee', e.target.value ? parseInt(e.target.value) : null)} /></div>
          <div><label className={labelClass}>Date accouchement</label><input type="date" className={inputClass} value={dossier.date_accouchement} onChange={e => handleChange('date_accouchement', e.target.value)} /></div>
          <div><label className={labelClass}>Date entrée</label><input type="date" className={inputClass} value={dossier.date_entree} onChange={e => handleChange('date_entree', e.target.value)} /></div>
          <div><label className={labelClass}>Date sortie</label><input type="date" className={inputClass} value={dossier.date_sortie} onChange={e => handleChange('date_sortie', e.target.value)} /></div>
          <div><label className={labelClass}>Nom de jeune fille</label><input type="text" className={inputClass} value={dossier.nom_jeune_fille} onChange={e => handleChange('nom_jeune_fille', e.target.value)} /></div>
          <div><label className={labelClass}>Prénoms</label><input type="text" className={inputClass} value={dossier.prenoms} onChange={e => handleChange('prenoms', e.target.value)} /></div>
          <div><label className={labelClass}>Date de naissance</label><input type="date" className={inputClass} value={dossier.date_naissance} onChange={e => handleChange('date_naissance', e.target.value)} /></div>
          <div><label className={labelClass}>Nationalité</label><input type="text" className={inputClass} value={dossier.nationalite} onChange={e => handleChange('nationalite', e.target.value)} /></div>
          <div><label className={labelClass}>Profession</label><input type="text" className={inputClass} value={dossier.profession} onChange={e => handleChange('profession', e.target.value)} /></div>
          <div><label className={labelClass}>GS</label><input type="text" className={inputClass} value={dossier.gs} onChange={e => handleChange('gs', e.target.value)} /></div>
          <div><label className={labelClass}>Rh</label><input type="text" className={inputClass} value={dossier.rh} onChange={e => handleChange('rh', e.target.value)} /></div>
          <div><label className={labelClass}>Taille</label><input type="text" className={inputClass} value={dossier.taille} onChange={e => handleChange('taille', e.target.value)} placeholder="ex: 1m65" /></div>
          <div><label className={labelClass}>Village / Quartier</label><input type="text" className={inputClass} value={dossier.village_quartier} onChange={e => handleChange('village_quartier', e.target.value)} /></div>
          <div><label className={labelClass}>Carré N°</label><input type="text" className={inputClass} value={dossier.carre_no} onChange={e => handleChange('carre_no', e.target.value)} /></div>
          <div><label className={labelClass}>Rue N°</label><input type="text" className={inputClass} value={dossier.rue_no} onChange={e => handleChange('rue_no', e.target.value)} /></div>
          <div><label className={labelClass}>Maison</label><input type="text" className={inputClass} value={dossier.maison} onChange={e => handleChange('maison', e.target.value)} /></div>
          <div><label className={labelClass}>Téléphone</label><input type="text" className={inputClass} value={dossier.telephone} onChange={e => handleChange('telephone', e.target.value)} /></div>
        </div>
      </section>

      {/* SECTION 3: Procréateur */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-user-heart-line text-rose-500"></i>
          Procréateur
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><label className={labelClass}>Nom</label><input type="text" className={inputClass} value={dossier.procreateur_nom} onChange={e => handleChange('procreateur_nom', e.target.value)} /></div>
          <div><label className={labelClass}>Prénoms</label><input type="text" className={inputClass} value={dossier.procreateur_prenoms} onChange={e => handleChange('procreateur_prenoms', e.target.value)} /></div>
          <div><label className={labelClass}>Profession</label><input type="text" className={inputClass} value={dossier.procreateur_profession} onChange={e => handleChange('procreateur_profession', e.target.value)} /></div>
          <div><label className={labelClass}>GS</label><input type="text" className={inputClass} value={dossier.procreateur_gs} onChange={e => handleChange('procreateur_gs', e.target.value)} /></div>
          <div><label className={labelClass}>RH</label><input type="text" className={inputClass} value={dossier.procreateur_rh} onChange={e => handleChange('procreateur_rh', e.target.value)} /></div>
          <div><label className={labelClass}>Électrophorèse de l'hémoglobine</label><input type="text" className={inputClass} value={dossier.procreateur_electrophorese} onChange={e => handleChange('procreateur_electrophorese', e.target.value)} /></div>
          <div><label className={labelClass}>Sérologie du conjoint</label><input type="text" className={inputClass} value={dossier.procreateur_serologie} onChange={e => handleChange('procreateur_serologie', e.target.value)} /></div>
          <div><label className={labelClass}>Contact</label><input type="text" className={inputClass} value={dossier.procreateur_contact} onChange={e => handleChange('procreateur_contact', e.target.value)} /></div>
          <div><label className={labelClass}>Personne à contacter</label><input type="text" className={inputClass} value={dossier.procreateur_personne_contacter} onChange={e => handleChange('procreateur_personne_contacter', e.target.value)} /></div>
          <div><label className={labelClass}>Adresse</label><input type="text" className={inputClass} value={dossier.procreateur_adresse} onChange={e => handleChange('procreateur_adresse', e.target.value)} /></div>
          <div><label className={labelClass}>Téléphone</label><input type="text" className={inputClass} value={dossier.procreateur_tel} onChange={e => handleChange('procreateur_tel', e.target.value)} /></div>
          <div className="flex items-end gap-6 pb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.procreateur_venue_elle_meme} onChange={e => handleChange('procreateur_venue_elle_meme', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Venue d'elle-même ?</span>
            </label>
          </div>
          <div className="flex items-end gap-6 pb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.procreateur_referee} onChange={e => handleChange('procreateur_referee', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Référée ?</span>
            </label>
          </div>
          {dossier.procreateur_referee && (
            <div><label className={labelClass}>Si oui, par qui ?</label><input type="text" className={inputClass} value={dossier.procreateur_referee_par} onChange={e => handleChange('procreateur_referee_par', e.target.value)} /></div>
          )}
        </div>
      </section>

      {/* SECTION 4: Antécédents */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-health-book-line text-rose-500"></i>
          Antécédents
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="sm:col-span-2"><label className={labelClass}>Maladies et opérations</label><textarea className={inputClass} rows={2} value={dossier.maladies_operations} onChange={e => handleChange('maladies_operations', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={labelClass}>Traitements en cours</label><textarea className={inputClass} rows={2} value={dossier.traitements_en_cours} onChange={e => handleChange('traitements_en_cours', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={labelClass}>Allergies</label><textarea className={inputClass} rows={2} value={dossier.allergies} onChange={e => handleChange('allergies', e.target.value)} /></div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.transfusions_anterieures} onChange={e => handleChange('transfusions_anterieures', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Transfusions antérieures</span>
            </label>
          </div>
          {dossier.transfusions_anterieures && (
            <div><label className={labelClass}>Nombre</label><input type="number" className={inputClass} value={dossier.transfusions_nombre || ''} onChange={e => handleChange('transfusions_nombre', e.target.value ? parseInt(e.target.value) : null)} /></div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <div><label className={labelClass}>Gestité</label><input type="number" className={inputClass} value={dossier.gestite || ''} onChange={e => handleChange('gestite', e.target.value ? parseInt(e.target.value) : null)} /></div>
          <div><label className={labelClass}>Parité</label><input type="number" className={inputClass} value={dossier.parite || ''} onChange={e => handleChange('parite', e.target.value ? parseInt(e.target.value) : null)} /></div>
          <div><label className={labelClass}>Nbre avortements</label><input type="number" className={inputClass} value={dossier.nombre_avortements || ''} onChange={e => handleChange('nombre_avortements', e.target.value ? parseInt(e.target.value) : null)} /></div>
          <div><label className={labelClass}>Enfants vivants</label><input type="number" className={inputClass} value={dossier.nombre_enfants_vivants || ''} onChange={e => handleChange('nombre_enfants_vivants', e.target.value ? parseInt(e.target.value) : null)} /></div>
          <div><label className={labelClass}>Enfants décédés</label><input type="number" className={inputClass} value={dossier.nombre_enfants_decede || ''} onChange={e => handleChange('nombre_enfants_decede', e.target.value ? parseInt(e.target.value) : null)} /></div>
          <div><label className={labelClass}>DDR</label><input type="date" className={inputClass} value={dossier.ddr} onChange={e => handleChange('ddr', e.target.value)} /></div>
          <div><label className={labelClass}>Date probable accouchement</label><input type="date" className={inputClass} value={dossier.dpa} onChange={e => handleChange('dpa', e.target.value)} /></div>
        </div>
      </section>

      {/* SECTION 5: Tableau Grossesses Antérieures */}
      <section className="border-t border-gray-100 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <i className="ri-table-line text-rose-500"></i>
            Grossesses Antérieures
          </h2>
          <button onClick={addGrossesse} type="button" className="px-3 py-1.5 text-xs font-semibold bg-rose-50 text-rose-600 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line mr-1"></i>Ajouter
          </button>
        </div>
        {grossesses.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Aucune grossesse antérieure enregistrée.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-r border-gray-200">Année</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-r border-gray-200">Grossesse</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-r border-gray-200">Évolution</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-r border-gray-200">Poids</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-r border-gray-200">Sexe</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-r border-gray-200">État des enfants</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {grossesses.map((g, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50/50">
                    <td className="px-3 py-1.5 border-r border-gray-100"><input type="text" className="w-full text-sm bg-transparent focus:outline-none" value={g.annee} onChange={e => updateGrossesse(i, 'annee', e.target.value)} /></td>
                    <td className="px-3 py-1.5 border-r border-gray-100"><input type="text" className="w-full text-sm bg-transparent focus:outline-none" value={g.grossesse} onChange={e => updateGrossesse(i, 'grossesse', e.target.value)} /></td>
                    <td className="px-3 py-1.5 border-r border-gray-100"><input type="text" className="w-full text-sm bg-transparent focus:outline-none" value={g.evolution} onChange={e => updateGrossesse(i, 'evolution', e.target.value)} /></td>
                    <td className="px-3 py-1.5 border-r border-gray-100"><input type="text" className="w-full text-sm bg-transparent focus:outline-none" value={g.poids} onChange={e => updateGrossesse(i, 'poids', e.target.value)} /></td>
                    <td className="px-3 py-1.5 border-r border-gray-100"><input type="text" className="w-full text-sm bg-transparent focus:outline-none" value={g.sexe} onChange={e => updateGrossesse(i, 'sexe', e.target.value)} /></td>
                    <td className="px-3 py-1.5 border-r border-gray-100"><input type="text" className="w-full text-sm bg-transparent focus:outline-none" value={g.etat_enfants} onChange={e => updateGrossesse(i, 'etat_enfants', e.target.value)} /></td>
                    <td className="px-2 py-1.5 text-center">
                      <button type="button" onClick={() => removeGrossesse(i)} className="text-red-400 hover:text-red-600 cursor-pointer"><i className="ri-delete-bin-line"></i></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* SECTION 6: Facteurs de surveillance */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-alert-line text-rose-500"></i>
          Facteurs de Surveillance
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {FACTEURS_RISQUE_OPTIONS.map(facteur => (
            <label key={facteur} className="flex items-center gap-2 cursor-pointer hover:bg-rose-50 px-2 py-1.5 rounded-lg transition-colors">
              <input type="checkbox" checked={dossier.facteurs_risque.includes(facteur)} onChange={() => toggleFacteurRisque(facteur)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">{facteur}</span>
            </label>
          ))}
        </div>
        <div className="mt-3">
          <label className={labelClass}>Autres facteurs</label>
          <input type="text" className={inputClass} value={dossier.autres_facteurs} onChange={e => handleChange('autres_facteurs', e.target.value)} />
        </div>
      </section>

      {/* SECTION 7: Examens complémentaires */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-microscope-line text-rose-500"></i>
          Examens Complémentaires
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <div><label className={labelClass}>GS</label><input type="text" className={inputClass} value={dossier.examens_gs} onChange={e => handleChange('examens_gs', e.target.value)} /></div>
          <div><label className={labelClass}>Rh</label><input type="text" className={inputClass} value={dossier.examens_rh} onChange={e => handleChange('examens_rh', e.target.value)} /></div>
          <div><label className={labelClass}>Test de Coobs direct</label><input type="text" className={inputClass} value={dossier.coobs_direct} onChange={e => handleChange('coobs_direct', e.target.value)} /></div>
          <div><label className={labelClass}>TPHA</label><input type="text" className={inputClass} value={dossier.tpha} onChange={e => handleChange('tpha', e.target.value)} /></div>
          <div><label className={labelClass}>VDRL</label><input type="text" className={inputClass} value={dossier.vdrl} onChange={e => handleChange('vdrl', e.target.value)} /></div>
          <div><label className={labelClass}>Électrophorèse de l'Hb</label><input type="text" className={inputClass} value={dossier.electrophorese} onChange={e => handleChange('electrophorese', e.target.value)} /></div>
          <div><label className={labelClass}>Toxoplasme IgG</label><input type="text" className={inputClass} value={dossier.toxo_igg} onChange={e => handleChange('toxo_igg', e.target.value)} /></div>
          <div><label className={labelClass}>Toxoplasme IgM</label><input type="text" className={inputClass} value={dossier.toxo_igm} onChange={e => handleChange('toxo_igm', e.target.value)} /></div>
          <div><label className={labelClass}>Rubéole IgG</label><input type="text" className={inputClass} value={dossier.rubeole_igg} onChange={e => handleChange('rubeole_igg', e.target.value)} /></div>
          <div><label className={labelClass}>Rubéole IgM</label><input type="text" className={inputClass} value={dossier.rubeole_igm} onChange={e => handleChange('rubeole_igm', e.target.value)} /></div>
          <div><label className={labelClass}>Glycémie à jeun GP75</label><input type="text" className={inputClass} value={dossier.glycemie_jeun} onChange={e => handleChange('glycemie_jeun', e.target.value)} /></div>
          <div><label className={labelClass}>HIV 1</label><input type="text" className={inputClass} value={dossier.hiv1} onChange={e => handleChange('hiv1', e.target.value)} /></div>
          <div><label className={labelClass}>HIV 2</label><input type="text" className={inputClass} value={dossier.hiv2} onChange={e => handleChange('hiv2', e.target.value)} /></div>
          <div><label className={labelClass}>ECBU</label><input type="text" className={inputClass} value={dossier.ecbu} onChange={e => handleChange('ecbu', e.target.value)} /></div>
          <div><label className={labelClass}>Taux d'hémoglobine</label><input type="text" className={inputClass} value={dossier.taux_hemoglobine} onChange={e => handleChange('taux_hemoglobine', e.target.value)} /></div>
          <div><label className={labelClass}>Hématocrite</label><input type="text" className={inputClass} value={dossier.hematocrite} onChange={e => handleChange('hematocrite', e.target.value)} /></div>
          <div><label className={labelClass}>Plaquettes</label><input type="text" className={inputClass} value={dossier.plaquettes} onChange={e => handleChange('plaquettes', e.target.value)} /></div>
          <div><label className={labelClass}>Hépatite B</label><input type="text" className={inputClass} value={dossier.hepatite_b} onChange={e => handleChange('hepatite_b', e.target.value)} /></div>
          <div className="sm:col-span-2 lg:col-span-2">
            <label className={labelClass}>Autres examens</label>
            <input type="text" className={inputClass} value={dossier.autres_examens} onChange={e => handleChange('autres_examens', e.target.value)} />
          </div>
        </div>
      </section>

      {/* SECTION 8: HIV Positif */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-virus-line text-rose-500"></i>
          HIV Positif / Syphilis
        </h2>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dossier.hiv_positif_arv} onChange={e => handleChange('hiv_positif_arv', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
            <span className="text-sm text-gray-700">Si HIV Positif, mise sous ARV</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dossier.syphilis_positif_traitement} onChange={e => handleChange('syphilis_positif_traitement', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
            <span className="text-sm text-gray-700">Si Syphilis positif, traitement</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dossier.mise_sous_ctm} onChange={e => handleChange('mise_sous_ctm', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
            <span className="text-sm text-gray-700">Mise sous CTM</span>
          </label>
        </div>
      </section>

      {/* SECTION 9: Soins préventifs - VAT */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-syringe-line text-rose-500"></i>
          Soins Préventifs - Vaccination VAT
        </h2>
        <div className="flex flex-wrap gap-4">
          {[1, 2, 3, 4, 5].map(n => (
            <label key={n} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={(dossier as Record<string, unknown>)[`vat${n}`] as boolean} onChange={e => handleChange(`vat${n}`, e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700 font-medium">VAT {n}</span>
            </label>
          ))}
        </div>
      </section>

      {/* SECTION 10: Droits fondamentaux */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-shield-check-line text-rose-500"></i>
          Vulgarisation des Dix Droits Fondamentaux
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {DROITS_FONDAMENTAUX_OPTIONS.map((droit, idx) => (
            <label key={idx} className="flex items-center gap-2 cursor-pointer hover:bg-rose-50 px-2 py-1.5 rounded-lg transition-colors">
              <input type="checkbox" checked={dossier.droits_fondamentaux.includes(droit)} onChange={() => toggleDroitFondamental(droit)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">{idx + 1}. {droit}</span>
            </label>
          ))}
        </div>
      </section>

      {/* SECTION 11: Plan d'accouchement */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-calendar-check-line text-rose-500"></i>
          Plan pour l'Accouchement
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><label className={labelClass}>Lieu de l'accouchement</label><input type="text" className={inputClass} value={dossier.plan_lieu_accouchement} onChange={e => handleChange('plan_lieu_accouchement', e.target.value)} /></div>
          <div><label className={labelClass}>Accoucheur qualifié certifié</label><input type="text" className={inputClass} value={dossier.plan_accoucheur_qualifie} onChange={e => handleChange('plan_accoucheur_qualifie', e.target.value)} /></div>
          <div><label className={labelClass}>Contact de l'accoucheur</label><input type="text" className={inputClass} value={dossier.plan_contact_accoucheur} onChange={e => handleChange('plan_contact_accoucheur', e.target.value)} /></div>
          <div><label className={labelClass}>Mode de transport</label><input type="text" className={inputClass} value={dossier.plan_mode_transport} onChange={e => handleChange('plan_mode_transport', e.target.value)} /></div>
          <div><label className={labelClass}>Accompagnement identifié</label><input type="text" className={inputClass} value={dossier.plan_accompagnement} onChange={e => handleChange('plan_accompagnement', e.target.value)} /></div>
          <div><label className={labelClass}>Garde des enfants à la maison</label><input type="text" className={inputClass} value={dossier.plan_garde_enfants} onChange={e => handleChange('plan_garde_enfants', e.target.value)} /></div>
          <div><label className={labelClass}>Décideur en absence du mari</label><input type="text" className={inputClass} value={dossier.plan_decideur_absence_mari} onChange={e => handleChange('plan_decideur_absence_mari', e.target.value)} /></div>
          <div><label className={labelClass}>Épargne pour accouchement</label><input type="text" className={inputClass} value={dossier.plan_epargne_accouchement} onChange={e => handleChange('plan_epargne_accouchement', e.target.value)} /></div>
          <div><label className={labelClass}>Donneur de sang prévu</label><input type="text" className={inputClass} value={dossier.plan_donneur_sang} onChange={e => handleChange('plan_donneur_sang', e.target.value)} /></div>
        </div>
      </section>

      {/* SECTION 12: Autres soins promotionnels + Classe des mères */}
      <section className="border-t border-gray-100 pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-information-line text-rose-500"></i>
              Autres Soins Promotionnels
            </h2>
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dossier.info_pf} onChange={e => handleChange('info_pf', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm text-gray-700">Information sur la PF</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dossier.reference_cpn} onChange={e => handleChange('reference_cpn', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm text-gray-700">Référence pour la CPN</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dossier.reference_accouchement} onChange={e => handleChange('reference_accouchement', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm text-gray-700">Référence pour accouchement</span>
              </label>
            </div>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-user-star-line text-rose-500"></i>
              Classe des Mères
            </h2>
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dossier.classe_1er_trimestre} onChange={e => handleChange('classe_1er_trimestre', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm text-gray-700">1er Trimestre</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dossier.classe_2e_trimestre} onChange={e => handleChange('classe_2e_trimestre', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm text-gray-700">2ème Trimestre</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dossier.classe_3e_trimestre} onChange={e => handleChange('classe_3e_trimestre', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm text-gray-700">3ème Trimestre</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dossier.classe_accouchement_recent} onChange={e => handleChange('classe_accouchement_recent', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm text-gray-700">Accouchement récent</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dossier.classe_accouchement_tardif} onChange={e => handleChange('classe_accouchement_tardif', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm text-gray-700">Accouchement tardif</span>
              </label>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 13: Consultation Prénatale */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-stethoscope-line text-rose-500"></i>
          Consultation Prénatale
        </h2>

        {/* Informations générales de la consultation */}
        <div className="bg-rose-50/50 rounded-xl border border-rose-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-rose-700 mb-3 flex items-center gap-2">
            <i className="ri-calendar-event-line"></i>
            Informations de la visite
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className={labelClass}>Date de la 1ère visite</label>
              <input type="date" className={inputClass} value={dossier.cpn_date_premiere_visite} onChange={e => handleChange('cpn_date_premiere_visite', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Trimestre</label>
              <select className={inputClass} value={dossier.cpn_trimestre} onChange={e => handleChange('cpn_trimestre', e.target.value)}>
                <option value="">Sélectionner</option>
                <option value="1er">1er Trimestre (obligatoire)</option>
                <option value="2ème">2ème Trimestre (obligatoire)</option>
                <option value="3ème">3ème Trimestre (obligatoire)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Date et heure d&apos;arrivée</label>
              <input type="datetime-local" className={inputClass} value={dossier.cpn_date_heure_arrivee} onChange={e => handleChange('cpn_date_heure_arrivee', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Heures de la CPNR</label>
              <input type="text" className={inputClass} value={dossier.cpn_heure_cpnr} onChange={e => handleChange('cpn_heure_cpnr', e.target.value)} placeholder="ex: 08h00 - 10h00" />
            </div>
            <div>
              <label className={labelClass}>Identité du prestataire</label>
              <input type="text" className={inputClass} value={dossier.cpn_prestataire_identite} onChange={e => handleChange('cpn_prestataire_identite', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Qualification du prestataire</label>
              <input type="text" className={inputClass} value={dossier.cpn_prestataire_qualification} onChange={e => handleChange('cpn_prestataire_qualification', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Terme de la grossesse</label>
              <input type="text" className={inputClass} value={dossier.cpn_terme_grossesse} onChange={e => handleChange('cpn_terme_grossesse', e.target.value)} placeholder="ex: 28 SA" />
            </div>
            <div>
              <label className={labelClass}>Résultat de l&apos;échographie</label>
              <input type="text" className={inputClass} value={dossier.cpn_resultat_echographie} onChange={e => handleChange('cpn_resultat_echographie', e.target.value)} />
            </div>
          </div>
          <div className="mt-3">
            <label className={labelClass}>Plainte</label>
            <textarea className={inputClass} rows={2} value={dossier.cpn_plainte} onChange={e => handleChange('cpn_plainte', e.target.value)} />
          </div>
        </div>

        {/* Etat général */}
        <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-4 mb-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <i className="ri-heart-pulse-line"></i>
            Etat Général
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            <div><label className={labelClass}>TA (mmHg)</label><input type="text" className={inputClass} value={dossier.cpn_ta} onChange={e => handleChange('cpn_ta', e.target.value)} placeholder="ex: 120/80" /></div>
            <div><label className={labelClass}>Pouls (bpm)</label><input type="text" className={inputClass} value={dossier.cpn_pouls} onChange={e => handleChange('cpn_pouls', e.target.value)} /></div>
            <div><label className={labelClass}>Température (°C)</label><input type="text" className={inputClass} value={dossier.cpn_temperature} onChange={e => handleChange('cpn_temperature', e.target.value)} /></div>
            <div><label className={labelClass}>Muqueuse</label><input type="text" className={inputClass} value={dossier.cpn_muqueuse} onChange={e => handleChange('cpn_muqueuse', e.target.value)} /></div>
            <div><label className={labelClass}>OMI</label><input type="text" className={inputClass} value={dossier.cpn_omi} onChange={e => handleChange('cpn_omi', e.target.value)} /></div>
            <div><label className={labelClass}>Conscience</label><input type="text" className={inputClass} value={dossier.cpn_conscience} onChange={e => handleChange('cpn_conscience', e.target.value)} /></div>
            <div><label className={labelClass}>Poids (kg)</label><input type="text" className={inputClass} value={dossier.cpn_poids} onChange={e => handleChange('cpn_poids', e.target.value)} /></div>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div><label className={labelClass}>IMC</label><input type="text" className={inputClass} value={dossier.cpn_imc} onChange={e => handleChange('cpn_imc', e.target.value)} /></div>
          </div>
        </div>

        {/* Test d'urine */}
        <div className="bg-amber-50/50 rounded-xl border border-amber-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-amber-700 mb-3 flex items-center gap-2">
            <i className="ri-test-tube-line"></i>
            Test d&apos;Urine (A: Albumine / S: Sucre / N: Nitrite / L: Leucocyte)
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className={labelClass}>Albumine</label><input type="text" className={inputClass} value={dossier.cpn_urine_albumine} onChange={e => handleChange('cpn_urine_albumine', e.target.value)} placeholder="A: ..." /></div>
            <div><label className={labelClass}>Sucre</label><input type="text" className={inputClass} value={dossier.cpn_urine_sucre} onChange={e => handleChange('cpn_urine_sucre', e.target.value)} placeholder="S: ..." /></div>
            <div><label className={labelClass}>Nitrite</label><input type="text" className={inputClass} value={dossier.cpn_urine_nitrite} onChange={e => handleChange('cpn_urine_nitrite', e.target.value)} placeholder="N: ..." /></div>
            <div><label className={labelClass}>Leucocyte</label><input type="text" className={inputClass} value={dossier.cpn_urine_leucocyte} onChange={e => handleChange('cpn_urine_leucocyte', e.target.value)} placeholder="L: ..." /></div>
          </div>
        </div>

        {/* Examens physiques */}
        <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-blue-700 mb-3 flex items-center gap-2">
            <i className="ri-body-scan-line"></i>
            Examens Physiques
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className={labelClass}>Examen des seins</label><input type="text" className={inputClass} value={dossier.cpn_examen_seins} onChange={e => handleChange('cpn_examen_seins', e.target.value)} /></div>
            <div><label className={labelClass}>Inspection</label><input type="text" className={inputClass} value={dossier.cpn_inspection} onChange={e => handleChange('cpn_inspection', e.target.value)} /></div>
            <div><label className={labelClass}>Mensuration HU</label><input type="text" className={inputClass} value={dossier.cpn_mensuration_hu} onChange={e => handleChange('cpn_mensuration_hu', e.target.value)} /></div>
            <div><label className={labelClass}>Palpation - Présentation</label><input type="text" className={inputClass} value={dossier.cpn_palpation_presentation} onChange={e => handleChange('cpn_palpation_presentation', e.target.value)} /></div>
            <div><label className={labelClass}>Palpation - Points urétéreux moyens</label><input type="text" className={inputClass} value={dossier.cpn_palpation_points_ureteraux} onChange={e => handleChange('cpn_palpation_points_ureteraux', e.target.value)} /></div>
            <div><label className={labelClass}>Auscultation BDCF</label><input type="text" className={inputClass} value={dossier.cpn_auscultation_bdcf} onChange={e => handleChange('cpn_auscultation_bdcf', e.target.value)} /></div>
            <div><label className={labelClass}>Etat de la vulve</label><input type="text" className={inputClass} value={dossier.cpn_etat_vulve} onChange={e => handleChange('cpn_etat_vulve', e.target.value)} /></div>
            <div><label className={labelClass}>Pose de spéculum</label><input type="text" className={inputClass} value={dossier.cpn_pose_speculum} onChange={e => handleChange('cpn_pose_speculum', e.target.value)} /></div>
            <div><label className={labelClass}>Toucher vaginal</label><input type="text" className={inputClass} value={dossier.cpn_toucher_vaginal} onChange={e => handleChange('cpn_toucher_vaginal', e.target.value)} /></div>
            <div><label className={labelClass}>Examen du bassin</label><input type="text" className={inputClass} value={dossier.cpn_examen_bassin} onChange={e => handleChange('cpn_examen_bassin', e.target.value)} /></div>
          </div>
        </div>

        {/* Diagnostic */}
        <div className="bg-red-50/50 rounded-xl border border-red-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
            <i className="ri-hospital-line"></i>
            Diagnostic
          </h3>
          <textarea className={inputClass} rows={3} value={dossier.cpn_diagnostic} onChange={e => handleChange('cpn_diagnostic', e.target.value)} placeholder="Diagnostic établi lors de cette consultation..." />
        </div>

        {/* Prévention */}
        <div className="bg-emerald-50/50 rounded-xl border border-emerald-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-emerald-700 mb-3 flex items-center gap-2">
            <i className="ri-shield-check-line"></i>
            Prévention
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
            <div><label className={labelClass}>TPI/SP 1ère dose</label><input type="text" className={inputClass} value={dossier.cpn_tpi_sp_dose1} onChange={e => handleChange('cpn_tpi_sp_dose1', e.target.value)} /></div>
            <div><label className={labelClass}>TPI/SP 2ème dose</label><input type="text" className={inputClass} value={dossier.cpn_tpi_sp_dose2} onChange={e => handleChange('cpn_tpi_sp_dose2', e.target.value)} /></div>
            <div><label className={labelClass}>TPI/SP 3ème dose</label><input type="text" className={inputClass} value={dossier.cpn_tpi_sp_dose3} onChange={e => handleChange('cpn_tpi_sp_dose3', e.target.value)} /></div>
            <div><label className={labelClass}>TPI/SP 4ème dose</label><input type="text" className={inputClass} value={dossier.cpn_tpi_sp_dose4} onChange={e => handleChange('cpn_tpi_sp_dose4', e.target.value)} /></div>
            <div><label className={labelClass}>TPI/SP 5ème dose</label><input type="text" className={inputClass} value={dossier.cpn_tpi_sp_dose5} onChange={e => handleChange('cpn_tpi_sp_dose5', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div><label className={labelClass}>Déparasitage</label><input type="text" className={inputClass} value={dossier.cpn_deparasitage} onChange={e => handleChange('cpn_deparasitage', e.target.value)} /></div>
            <div><label className={labelClass}>Fer 200mg/jour</label><input type="text" className={inputClass} value={dossier.cpn_fer_200mg} onChange={e => handleChange('cpn_fer_200mg', e.target.value)} /></div>
            <div><label className={labelClass}>Foldine 5mg/jour</label><input type="text" className={inputClass} value={dossier.cpn_foldine_5mg} onChange={e => handleChange('cpn_foldine_5mg', e.target.value)} /></div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dossier.cpn_dormi_sous_mild} onChange={e => handleChange('cpn_dormi_sous_mild', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
            <span className="text-sm text-gray-700">Avez-vous dormi sous MILD la nuit dernière ?</span>
          </label>
        </div>

        {/* Conseils et planification */}
        <div className="bg-purple-50/50 rounded-xl border border-purple-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-purple-700 mb-3 flex items-center gap-2">
            <i className="ri-nurse-line"></i>
            Conseils et Planification
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div><label className={labelClass}>Conseils nutritionnels</label><textarea className={inputClass} rows={2} value={dossier.cpn_conseils_nutritionnels} onChange={e => handleChange('cpn_conseils_nutritionnels', e.target.value)} /></div>
            <div><label className={labelClass}>Information sur les signes de danger</label><textarea className={inputClass} rows={2} value={dossier.cpn_signes_danger} onChange={e => handleChange('cpn_signes_danger', e.target.value)} /></div>
            <div><label className={labelClass}>Counseling sur PF</label><textarea className={inputClass} rows={2} value={dossier.cpn_counseling_pf} onChange={e => handleChange('cpn_counseling_pf', e.target.value)} /></div>
            <div><label className={labelClass}>Méthode choisie</label><input type="text" className={inputClass} value={dossier.cpn_methode_choisie} onChange={e => handleChange('cpn_methode_choisie', e.target.value)} /></div>
            <div className="sm:col-span-2"><label className={labelClass}>Conduite à tenir</label><textarea className={inputClass} rows={2} value={dossier.cpn_conduite_a_tenir} onChange={e => handleChange('cpn_conduite_a_tenir', e.target.value)} /></div>
          </div>
        </div>

        {/* RDV et suivi */}
        <div className="bg-rose-50/50 rounded-xl border border-rose-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-rose-700 mb-3 flex items-center gap-2">
            <i className="ri-calendar-check-line"></i>
            RDV et Suivi
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            <div><label className={labelClass}>RDV CPN</label><input type="text" className={inputClass} value={dossier.cpn_rdv_cpn} onChange={e => handleChange('cpn_rdv_cpn', e.target.value)} /></div>
            <div><label className={labelClass}>RDV Classe des mères</label><input type="text" className={inputClass} value={dossier.cpn_rdv_classe_meres} onChange={e => handleChange('cpn_rdv_classe_meres', e.target.value)} /></div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dossier.cpn_recherche_active} onChange={e => handleChange('cpn_recherche_active', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
            <span className="text-sm text-gray-700 font-medium">Recherche active (suivi actif du patient)</span>
          </label>
        </div>
      </section>

      {/* Barrière Accouchement */}
      {!accouchementEffectue ? (
        <section className="border-t-2 border-dashed border-amber-300 pt-6 pb-4">
          <div className="bg-amber-50/80 rounded-xl border border-amber-200 p-6 text-center">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-amber-100 rounded-full">
              <i className="ri-lock-line text-2xl text-amber-600"></i>
            </div>
            <h3 className="text-base font-bold text-amber-800 mb-2">
              Sections post-accouchement verrouillées
            </h3>
            <p className="text-sm text-amber-700 mb-4 max-w-lg mx-auto">
              Le Suivi de l&apos;Accouchement et la Fiche de Surveillance ne sont accessibles qu&apos;après avoir marqué l&apos;accouchement comme effectué dans une consultation.
            </p>
            <button
              type="button"
              onClick={handleMarquerAccouchement}
              disabled={marquageAccouchementLoading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-semibold text-sm whitespace-nowrap cursor-pointer disabled:opacity-60 shadow-sm"
            >
              {marquageAccouchementLoading ? (
                <><i className="ri-loader-4-line animate-spin"></i>Marquage en cours...</>
              ) : (
                <><i className="ri-heart-pulse-line"></i>Marquer l&apos;accouchement comme fait</>
              )}
            </button>
            <p className="text-xs text-amber-500 mt-3">
              <i className="ri-information-line mr-1"></i>
              Cette action marquera automatiquement une consultation liée à cette patiente comme "accouchement fait".
            </p>
          </div>
        </section>
      ) : (
        <>
          {/* Indicateur accouchement déverrouillé */}
          <div className="border-t border-gray-100 pt-4 pb-0">
            <div className="flex items-center gap-3 px-4 py-3 bg-green-50/80 rounded-xl border border-green-200">
              <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full">
                <i className="ri-check-line text-green-600"></i>
              </div>
              <div>
                <span className="text-sm font-semibold text-green-700">Accouchement effectué</span>
                <span className="text-xs text-green-500 ml-2">— Les sections post-accouchement sont déverrouillées</span>
              </div>
            </div>
          </div>

      {/* SECTION 14: Suivi de l'Accouchement */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-emotion-happy-line text-rose-500"></i>
          Suivi de l&apos;Accouchement
        </h2>

        {/* État du nouveau-né */}
        <div className="bg-pink-50/50 rounded-xl border border-pink-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-pink-700 mb-3 flex items-center gap-2">
            <i className="ri-heart-pulse-line"></i>
            État du Nouveau-Né
          </h3>
          <div className="flex flex-wrap gap-6 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.etat_nouveau_ne_vivant} onChange={e => handleChange('etat_nouveau_ne_vivant', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Vivant</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.etat_nouveau_ne_respiration_spontanee} onChange={e => handleChange('etat_nouveau_ne_respiration_spontanee', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Respiration spontanée</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.etat_nouveau_ne_reanimation} onChange={e => handleChange('etat_nouveau_ne_reanimation', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Réanimation</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.etat_nouveau_ne_ictere} onChange={e => handleChange('etat_nouveau_ne_ictere', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Ictère du nouveau-né</span>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div><label className={labelClass}>Durée réanimation</label><input type="text" className={inputClass} value={dossier.etat_nouveau_ne_duree_reanimation} onChange={e => handleChange('etat_nouveau_ne_duree_reanimation', e.target.value)} /></div>
            <div><label className={labelClass}>Poids (Kg)</label><input type="text" className={inputClass} value={dossier.poids_nouveau_ne} onChange={e => handleChange('poids_nouveau_ne', e.target.value)} /></div>
            <div><label className={labelClass}>Taille (cm)</label><input type="text" className={inputClass} value={dossier.taille_nouveau_ne} onChange={e => handleChange('taille_nouveau_ne', e.target.value)} /></div>
            <div><label className={labelClass}>PT (cm)</label><input type="text" className={inputClass} value={dossier.pt_nouveau_ne} onChange={e => handleChange('pt_nouveau_ne', e.target.value)} /></div>
            <div><label className={labelClass}>PC (cm)</label><input type="text" className={inputClass} value={dossier.pc_nouveau_ne} onChange={e => handleChange('pc_nouveau_ne', e.target.value)} /></div>
            <div><label className={labelClass}>Sexe (M/F)</label><select className={inputClass} value={dossier.sexe_nouveau_ne} onChange={e => handleChange('sexe_nouveau_ne', e.target.value)}><option value="">-</option><option value="M">M</option><option value="F">F</option></select></div>
            <div><label className={labelClass}>Semaine de grossesse</label><input type="text" className={inputClass} value={dossier.semaine_grossesse} onChange={e => handleChange('semaine_grossesse', e.target.value)} placeholder="ex: 38 SA" /></div>
          </div>

          {/* Tableau APGAR */}
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-r border-gray-200">APGAR</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 border-r border-gray-200">0</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 border-r border-gray-200">1</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 border-r border-gray-200">2</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 border-r border-gray-200">1er min</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 border-r border-gray-200">5ème min</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">10ème min</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Cœur', f1: 'Absent', f2: '&lt;100', f3: '&gt;100', vals: ['apgar_coeur_1min', 'apgar_coeur_5min', 'apgar_coeur_10min'] },
                  { label: 'Respiration', f1: 'Absent', f2: 'Faible', f3: 'Normal', vals: ['apgar_respiration_1min', 'apgar_respiration_5min', 'apgar_respiration_10min'] },
                  { label: 'Tonus', f1: 'Absent', f2: 'Mvt', f3: 'Normal', vals: ['apgar_tonus_1min', 'apgar_tonus_5min', 'apgar_tonus_10min'] },
                  { label: 'Couleur', f1: 'Pâle', f2: 'Cyanosé', f3: 'Rosé', vals: ['apgar_couleur_1min', 'apgar_couleur_5min', 'apgar_couleur_10min'] },
                  { label: 'Réflexes', f1: 'Absent', f2: 'Faible', f3: 'Normal', vals: ['apgar_reflexes_1min', 'apgar_reflexes_5min', 'apgar_reflexes_10min'] },
                ].map((row) => (
                  <tr key={row.label} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium text-gray-700 border-r border-gray-100">{row.label}</td>
                    <td className="px-3 py-2 text-center text-xs text-gray-500 border-r border-gray-100">{row.f1}</td>
                    <td className="px-3 py-2 text-center text-xs text-gray-500 border-r border-gray-100">{row.f2}</td>
                    <td className="px-3 py-2 text-center text-xs text-gray-500 border-r border-gray-100">{row.f3}</td>
                    {row.vals.map((v) => (
                      <td key={v} className="px-3 py-2 border-r border-gray-100">
                        <input type="text" className="w-full text-sm text-center bg-transparent focus:outline-none border border-gray-200 rounded" value={(dossier as Record<string, unknown>)[v] as string} onChange={e => handleChange(v, e.target.value)} />
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t border-gray-200 bg-gray-50 font-semibold">
                  <td className="px-3 py-2 text-gray-700 border-r border-gray-200" colSpan={4}>Totaux</td>
                  <td className="px-3 py-2 border-r border-gray-100"><input type="text" className="w-full text-sm text-center bg-transparent focus:outline-none border border-gray-200 rounded font-bold" value={dossier.apgar_total_1min} onChange={e => handleChange('apgar_total_1min', e.target.value)} /></td>
                  <td className="px-3 py-2 border-r border-gray-100"><input type="text" className="w-full text-sm text-center bg-transparent focus:outline-none border border-gray-200 rounded font-bold" value={dossier.apgar_total_5min} onChange={e => handleChange('apgar_total_5min', e.target.value)} /></td>
                  <td className="px-3 py-2"><input type="text" className="w-full text-sm text-center bg-transparent focus:outline-none border border-gray-200 rounded font-bold" value={dossier.apgar_total_10min} onChange={e => handleChange('apgar_total_10min', e.target.value)} /></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mort-né */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={dossier.mort_ne_macre} onChange={e => handleChange('mort_ne_macre', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                  <span className="text-sm font-semibold text-gray-700">Mort-né macéré</span>
                </label>
              </div>
              {dossier.mort_ne_macre && (
                <div className="grid grid-cols-3 gap-2">
                  <div><label className={labelClass}>1er degré</label><input type="text" className={inputClass} value={dossier.mort_ne_macre_1erdegre} onChange={e => handleChange('mort_ne_macre_1erdegre', e.target.value)} /></div>
                  <div><label className={labelClass}>2ème degré</label><input type="text" className={inputClass} value={dossier.mort_ne_macre_2emedegre} onChange={e => handleChange('mort_ne_macre_2emedegre', e.target.value)} /></div>
                  <div><label className={labelClass}>3ème degré</label><input type="text" className={inputClass} value={dossier.mort_ne_macre_3emedegre} onChange={e => handleChange('mort_ne_macre_3emedegre', e.target.value)} /></div>
                </div>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={dossier.mort_ne_frais} onChange={e => handleChange('mort_ne_frais', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                  <span className="text-sm font-semibold text-gray-700">Mort-né frais</span>
                </label>
              </div>
              {dossier.mort_ne_frais && (
                <div className="grid grid-cols-3 gap-2">
                  <div><label className={labelClass}>1er degré</label><input type="text" className={inputClass} value={dossier.mort_ne_frais_1erdegre} onChange={e => handleChange('mort_ne_frais_1erdegre', e.target.value)} /></div>
                  <div><label className={labelClass}>2ème degré</label><input type="text" className={inputClass} value={dossier.mort_ne_frais_2emedegre} onChange={e => handleChange('mort_ne_frais_2emedegre', e.target.value)} /></div>
                  <div><label className={labelClass}>3ème degré</label><input type="text" className={inputClass} value={dossier.mort_ne_frais_3emedegre} onChange={e => handleChange('mort_ne_frais_3emedegre', e.target.value)} /></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Soins au nouveau-né */}
        <div className="bg-amber-50/50 rounded-xl border border-amber-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-amber-700 mb-3 flex items-center gap-2">
            <i className="ri-nurse-line"></i>
            Soins au Nouveau-Né
          </h3>
          <div className="flex flex-wrap gap-6 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.soins_mise_sein_precoce} onChange={e => handleChange('soins_mise_sein_precoce', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Mise au sein précoce</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.soins_collyre} onChange={e => handleChange('soins_collyre', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Collyre (une seule fois)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.soins_arv_mere_seropositive} onChange={e => handleChange('soins_arv_mere_seropositive', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">ARV si mère séropositive</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.soins_anomalies} onChange={e => handleChange('soins_anomalies', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Anomalies</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.soins_examen_systematique} onChange={e => handleChange('soins_examen_systematique', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Examen systématique du nouveau-né</span>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div><label className={labelClass}>Vir (1mg IM)</label><input type="text" className={inputClass} value={dossier.soins_arv_vitamine} onChange={e => handleChange('soins_arv_vitamine', e.target.value)} placeholder="Vitamine" /></div>
            <div><label className={labelClass}>K1 (2mg Orale)</label><input type="text" className={inputClass} value={dossier.soins_arv_k1} onChange={e => handleChange('soins_arv_k1', e.target.value)} /></div>
          </div>
          {dossier.soins_anomalies && (
            <div><label className={labelClass}>Si oui, préciser les anomalies</label><textarea className={inputClass} rows={2} value={dossier.soins_anomalies_details} onChange={e => handleChange('soins_anomalies_details', e.target.value)} /></div>
          )}
        </div>

        {/* Délivrance */}
        <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-blue-700 mb-3 flex items-center gap-2">
            <i className="ri-hospital-line"></i>
            Délivrance
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div><label className={labelClass}>Heure de délivrance</label><input type="time" className={inputClass} value={dossier.delivrance_heure} onChange={e => handleChange('delivrance_heure', e.target.value)} /></div>
            <div><label className={labelClass}>Quantité de sang (ml)</label><input type="text" className={inputClass} value={dossier.delivrance_quantite_sang} onChange={e => handleChange('delivrance_quantite_sang', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.delivrance_naturelle} onChange={e => handleChange('delivrance_naturelle', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Naturelle</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.delivrance_artificielle} onChange={e => handleChange('delivrance_artificielle', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Artificielle</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.delivrance_assistee_gatpa} onChange={e => handleChange('delivrance_assistee_gatpa', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Assistée (GATPA)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.delivrance_oxytocine_10ui} onChange={e => handleChange('delivrance_oxytocine_10ui', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Oxytocine 10 UI 1ère mn</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.delivrance_traction_controlee} onChange={e => handleChange('delivrance_traction_controlee', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Traction contrôlée</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.delivrance_massage_uterin} onChange={e => handleChange('delivrance_massage_uterin', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Massage utérin</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.delivrance_hemorragie} onChange={e => handleChange('delivrance_hemorragie', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Hémorragie</span>
            </label>
          </div>
        </div>

        {/* Placenta */}
        <div className="bg-rose-50/50 rounded-xl border border-rose-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-rose-700 mb-3 flex items-center gap-2">
            <i className="ri-test-tube-line"></i>
            Placenta
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.placenta_cotyledons_integres} onChange={e => handleChange('placenta_cotyledons_integres', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Cotylédons intègres</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.placenta_presence_cupule} onChange={e => handleChange('placenta_presence_cupule', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Présence de cupule</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.placenta_examen_face_maternelle} onChange={e => handleChange('placenta_examen_face_maternelle', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Examen face maternelle</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.placenta_examen_face_foetale} onChange={e => handleChange('placenta_examen_face_foetale', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Examen face foetale</span>
            </label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div><label className={labelClass}>Grand côté (cm)</label><input type="text" className={inputClass} value={dossier.placenta_grand_cote} onChange={e => handleChange('placenta_grand_cote', e.target.value)} /></div>
            <div><label className={labelClass}>Insertion du cordon</label><input type="text" className={inputClass} value={dossier.placenta_insertion_cordon} onChange={e => handleChange('placenta_insertion_cordon', e.target.value)} /></div>
            <div><label className={labelClass}>Petit côté (cm)</label><input type="text" className={inputClass} value={dossier.placenta_petit_cote} onChange={e => handleChange('placenta_petit_cote', e.target.value)} /></div>
            <div><label className={labelClass}>Longueur du cordon (cm)</label><input type="text" className={inputClass} value={dossier.placenta_longueur_cordon} onChange={e => handleChange('placenta_longueur_cordon', e.target.value)} /></div>
            <div><label className={labelClass}>Poids du placenta (g)</label><input type="text" className={inputClass} value={dossier.placenta_poids} onChange={e => handleChange('placenta_poids', e.target.value)} /></div>
          </div>
        </div>

        {/* Issue */}
        <div className="bg-red-50/50 rounded-xl border border-red-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
            <i className="ri-health-book-line"></i>
            Issue
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.issue_femme_referee} onChange={e => handleChange('issue_femme_referee', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Femme référée</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.issue_femme_decedee} onChange={e => handleChange('issue_femme_decedee', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Femme décédée</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.issue_nouveau_ne_referee} onChange={e => handleChange('issue_nouveau_ne_referee', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Nouveau-né référé</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.issue_nouveau_ne_decede} onChange={e => handleChange('issue_nouveau_ne_decede', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Nouveau-né décédé</span>
            </label>
          </div>
        </div>

        {/* Sensibilisation */}
        <div className="bg-purple-50/50 rounded-xl border border-purple-100 p-4 mb-5">
          <h3 className="text-sm font-bold text-purple-700 mb-3 flex items-center gap-2">
            <i className="ri-user-voice-line"></i>
            Sensibilisation
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div className="border border-gray-200 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input type="checkbox" checked={dossier.sensib_pf} onChange={e => handleChange('sensib_pf', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm font-semibold text-gray-700">PF : Oui</span>
              </label>
              {dossier.sensib_pf && (
                <div><label className={labelClass}>Si oui, Méthode choisie</label><input type="text" className={inputClass} value={dossier.sensib_methode_choisie} onChange={e => handleChange('sensib_methode_choisie', e.target.value)} /></div>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input type="checkbox" checked={dossier.sensib_allaitement} onChange={e => handleChange('sensib_allaitement', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm font-semibold text-gray-700">Allaitement : Oui</span>
              </label>
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input type="checkbox" checked={dossier.sensib_acceptation} onChange={e => handleChange('sensib_acceptation', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
                <span className="text-sm font-semibold text-gray-700">Acceptation : Oui</span>
              </label>
              {!dossier.sensib_acceptation && (
                <div><label className={labelClass}>Si non, préciser le motif</label><input type="text" className={inputClass} value={dossier.sensib_si_non_motif} onChange={e => handleChange('sensib_si_non_motif', e.target.value)} /></div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.sensib_carte_infantile_remplie} onChange={e => handleChange('sensib_carte_infantile_remplie', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Carte infantile remplie</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dossier.sensib_vitamine_a_accouchee} onChange={e => handleChange('sensib_vitamine_a_accouchee', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400" />
              <span className="text-sm text-gray-700">Vitamine A à l&apos;accouchée</span>
            </label>
          </div>
        </div>
      </section>

      {/* SECTION 15: Fiche de Surveillance Intégrée */}
      <section className="border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-file-list-line text-rose-500"></i>
          Fiche de Surveillance Intégrée de l&apos;Accouchée et du Nouveau-né
        </h2>
        <FicheSurveillanceSection
          data={surveillance}
          onChange={setSurveillance}
        />
      </section>
        </>
      )}

      {/* Save Button */}
      <div className="border-t border-gray-100 pt-6 flex items-center justify-end gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors font-semibold text-sm whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          {saving ? (
            <><i className="ri-loader-4-line animate-spin mr-2"></i>Enregistrement...</>
          ) : (
            <><i className="ri-save-line mr-2"></i>Enregistrer le Dossier Rose</>
          )}
        </button>
      </div>
    </div>
  );
}